import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Box,
    Button,
    Select,
    MenuItem,
    FormControl,
    Modal,
    Fade,
    Backdrop,
  } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useDropzone } from 'react-dropzone';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import API_BASE_URL from '../config';
import './FileManagement.css';

const FileManagement = () => {
  const [mediaContent, setMediaContent] = useState([]);
  const [deviceModels, setDeviceModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('primaryColor') || '#007bff');
  const [secondaryColor, setSecondaryColor] = useState(localStorage.getItem('secondaryColor') || '#6c757d');
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setPrimaryColor(localStorage.getItem('primaryColor') || '#ffffff');
      setSecondaryColor(localStorage.getItem('secondaryColor') || '#000000');
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 1000);
  }, []);

  const textColor = darkMode ? '#ffffff' : '#000000';


  useEffect(() => {
    fetchMediaContent();
    fetchDeviceTypes();
  }, []);

  const handleOpenModal = (imageUrl) => {
      setSelectedImage(imageUrl);
      setOpenModal(true);
  };

  const handleCloseModal = () => {
      setOpenModal(false);
      setSelectedImage(null);
  };

  const fetchDeviceTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/device-types`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setDeviceModels(data);
      
    } catch (error) {
      console.error("Error fetching media files:", error);
    }
  };

  const fetchMediaContent = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/media-files`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setMediaContent(data);
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching media files:", error);
      setLoading(false);
    }
  };

  const onDrop = useCallback(acceptedFiles => {
    setUploadedFiles(acceptedFiles.map(file => ({
        file,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        status: 'ready',
        customerId: -1 // Default to 'All' option for all files
    })));
}, []);

const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/gif' // Only accept GIF images
});

const handleUpload = async (fileInfo) => {
  console.log('Uploading file:', {
      fileName: fileInfo.file.name,
      fileType: fileInfo.file.type,
      fileSize: fileInfo.file.size,
      deviceModel: fileInfo.deviceModel
  });

  const formData = new FormData();
  formData.append('file', fileInfo.file);
  formData.append('customerId', fileInfo.customerId);
  formData.append('deviceModel', fileInfo.deviceModel);

  try {
      fileInfo.status = 'uploading';
      setUploadedFiles([...uploadedFiles]);

      const response = await fetch(`${API_BASE_URL}/users/upload-media`, {
          method: 'POST',
          body: formData,
          headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
      });

      if (!response.ok) {
        console.log(`HTTP status code: ${response.status}`);
        if (response.status === 409) {
            fileInfo.status = 'File Exists';
        } else if (response.status === 500) {
            fileInfo.status = 'Server Error';
        } else if (response.status === 415) {
            fileInfo.status = 'Unsupported Media Type';
        } else {
            fileInfo.status = 'Error';
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log(result.status);
      console.log('File upload complete:', result);

      // Update the fileInfo with the ID from the server
      fileInfo.status = 'Success';
      fileInfo.id = result.file.id;

      // Update the state to reflect the new ID
      setUploadedFiles([...uploadedFiles]);

    } catch (error) {
        console.error("Error uploading file:", error);
    } finally {
        setUploadedFiles([...uploadedFiles]);
    }
  };
  
  
  
  const getUniqueDeviceModels = () => {
    const uniqueModels = [];
    const modelNames = new Set();

    deviceModels.forEach(model => {
      if (!modelNames.has(model.name)) {
        modelNames.add(model.name);
        uniqueModels.push(model.name);
      }
    });

    return uniqueModels;
  };

  const handleDelete = (fileToDelete) => {
    setUploadedFiles(uploadedFiles.filter(file => file !== fileToDelete));
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchMediaContent();
  };

  const handleCustomerChange = (event, fileInfo) => {
    const updatedFiles = uploadedFiles.map(file => 
      file === fileInfo ? { ...file, customerId: event.target.value } : file
    );
    setUploadedFiles(updatedFiles);
  };

  const isFileDuplicate = (fileName) => {
    return mediaContent.some(media => media.file_name === `s:${fileName}`);
  };


  return (
    <Container className="admin-media-container" sx={{ backgroundColor: primaryColor, color: `${textColor} !important` }}>
      <Typography variant="h4" gutterBottom>
        Media Files
      </Typography>
      <Box 
        sx={{ 
          backgroundColor: primaryColor, 
          color: textColor, 
          border: `2px dashed ${darkMode ? '#ffffff' : '#000000'}` // Set border color based on mode
        }}
        {...getRootProps()} 
        className="dropzone"
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <Typography variant="h6">Drop the files here ...</Typography>
        ) : (
          <Typography variant="h6">
            Drag and drop files here, or click to select files. Only GIF images are allowed.
          </Typography>
        )}
        <Button className="upload-button" startIcon={<CloudUploadIcon />}>
          Select Files
        </Button>
      </Box>
      <Typography variant="body2" color="warning" sx={{ mt: 1, mb: 0, maxWidth: '100%', whiteSpace: 'nowrap' }}>
        Please make sure the resolution of media matches the resolution of the corresponding device model to avoid display error.
      </Typography>
      {uploadedFiles.length > 0 && (
        <TableContainer component={Paper} className="uploads-table" sx={{ backgroundColor: secondaryColor }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Preview</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Device Model</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uploadedFiles.map((fileInfo, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {fileInfo.preview ? (
                      <img src={fileInfo.preview} alt={fileInfo.file.name} className="preview-image" />
                    ) : (
                      <Typography>Preview not available</Typography>
                    )}
                  </TableCell>
                  <TableCell>{fileInfo.file.name}</TableCell>
                  <TableCell>
                    {isFileDuplicate(fileInfo.file.name) ? (
                        <span className="file-exists-status">File Exists</span>
                    ) : (
                        fileInfo.status
                    )}
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={fileInfo.deviceModel || ''}
                          onChange={(event) => {
                            const updatedFiles = uploadedFiles.map(file => 
                              file === fileInfo ? { ...file, deviceModel: event.target.value } : file
                            );
                            setUploadedFiles(updatedFiles);
                          }}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>
                            Please select a device model
                          </MenuItem>
                          {getUniqueDeviceModels().map((model, idx) => (
                            <MenuItem key={idx} value={model}>{model}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  <TableCell className="actions-cell">
                    <div className="action-buttons">
                        <Button 
                        className="upload-button"
                        onClick={() => handleUpload(fileInfo)} 
                        disabled={!fileInfo.deviceModel || fileInfo.status === 'uploading' || isFileDuplicate(fileInfo.file.name)}
                        startIcon={<CloudUploadIcon />}
                        style={{
                            backgroundColor: isFileDuplicate(fileInfo.file.name) ? 'grey' : '',
                            color: isFileDuplicate(fileInfo.file.name) ? 'white' : ''
                        }}
                        >
                        UPLOAD
                        </Button>
                      <Button 
                        className="delete-button"
                        onClick={() => handleDelete(fileInfo)}
                      >
                        X
                      </Button>
                      </div>
                    </TableCell>
                </TableRow>
                ))}
            </TableBody>
        </Table>
      </TableContainer>
    )}
  
  <Box className="existing-media-header" style={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important`}}>
    <Typography variant="h5">Existing Media</Typography>
    <Button className="refresh-button" onClick={handleRefresh} startIcon={<RefreshIcon />}>
        Refresh
    </Button>
</Box>
<TableContainer component={Paper} className="media-table" sx={{ backgroundColor: `${secondaryColor} !important`, color: `${textColor} !important`}}>
    <Table>
        <TableHead>
            <TableRow>
                <TableCell sx={{ color: `${textColor} !important` }}>Preview</TableCell>
                <TableCell sx={{ color: `${textColor} !important` }}>File Name</TableCell>
                <TableCell sx={{ color: `${textColor} !important` }}>File Size</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {loading ? (
                <TableRow>
                    <TableCell colSpan={5} align="center">
                        <CircularProgress />
                    </TableCell>
                </TableRow>
            ) : mediaContent.length > 0 ? (
                mediaContent.map((media) => (
                    <TableRow key={media.id}>
                        <TableCell sx={{ color: `${textColor} !important`}}>
                            {media.file_name.match(/\.(jpeg|jpg|gif|png)$/) ? (
                                <img 
                                    src={`${API_BASE_URL}${media.imageUrl}`} 
                                    alt={media.file_name} 
                                    className="preview-image" 
                                />
                            ) : (
                                <Typography>Preview not available</Typography>
                            )}
                        </TableCell>
                        <TableCell sx={{ color: `${textColor} !important` }}>{media.file_name}</TableCell>
                        <TableCell sx={{ color: `${textColor} !important` }}>{(media.file_size / 1024).toFixed(2)} KB</TableCell>
                    </TableRow>
                ))
            ) : (
                <TableRow>
                    <TableCell colSpan={5} align="center">
                        No media found
                    </TableCell>
                </TableRow>
            )}
        </TableBody>
    </Table>
</TableContainer>
    </Container>
  );
};


export default FileManagement;